import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { MassAssign, AssignedUserPayload } from './directie-serii.interface';

@Injectable({
    providedIn: 'root'
})
export class DirectieSeriiService {
    private token!: string;
    private basePath = environment.interop.basePath;

    private listUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.list;

    public listDTUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.listDT;

    private findURL =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.find;

    private storeURL =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.store;

    private deleteUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.delete;

    private addSerieUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.update;

    private removeSerieUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.remove;

    private listActiveRecordUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.listActiveRecord;

    private changeStatusActiveUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii
            .active;

    private changeStatusPublicUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii
            .public;

    private changeStatusBlocareUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii
            .blocare;

    private activeSwitchUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.activeSwitch;

    private updatePrefixUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.updatePrefix;

    private updateDirectiiCompartimenteSeriiUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.updateDirectiiCompartimenteSerii;

    private statusActiveMassUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.statusActiveMass;

    private statusPublicMassUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.statusPublicMass;

    private updateRecordsUrl =
        this.basePath +
        environment.interop.indoc.nomenclatoare.registratura.directieSerii.updateRecords;

    // Assigned Users URLs - Direct endpoints without environment
    public assignedUsersListDTUrl = this.basePath + 'api/directie-serii/assigned-users/list';
    private assignedUsersDeleteUrl = this.basePath + 'api/directie-serii/assigned-users/delete';
    private assignedUsersAddUrl = this.basePath + 'api/directie-serii/assigned-users/add';

    private httpOptions: any;

    constructor(private http: HttpClient) { }

    getListDT() {
        return this.http.get(this.listDTUrl, this.httpOptions);
    }

    findEntry(directieId: number, compartimentId: number) {
        const url = `${this.findURL}${directieId}/${compartimentId}`
        return this.http.get(url, this.httpOptions);
    }

    storeEntry(payload: any) {
        return this.http.post(this.storeURL, payload, this.httpOptions);
    }

    deleteEntry(directieId: number, compartimentId: number) {
        return this.http.delete(`${this.deleteUrl}${directieId}/${compartimentId}`, this.httpOptions);
    }

    addSerie(directieId: number, compartimentId: number, payload: any) {
        return this.http.put(`${this.addSerieUrl}/${directieId}/${compartimentId}`, payload, this.httpOptions);
    }

    removeSerie(directieId: number, compartimentId: number, serieId: number) {
        return this.http.delete(`${this.removeSerieUrl}${directieId}/${compartimentId}/${serieId}`, this.httpOptions);
    }

    getListActiveRecord() {
        return this.http.get(this.listActiveRecordUrl, this.httpOptions);
    }

    changeStatusActive(directieId: number, compartimentId: number | null) {
        return this.http.put(`${this.changeStatusActiveUrl}${directieId}/${compartimentId || -1}`, {}, this.httpOptions);
    }

    changeStatusPublic(directieId: number, compartimentId: number | null) {
        return this.http.put(`${this.changeStatusPublicUrl}${directieId}/${compartimentId || -1}`, {}, this.httpOptions);
    }

    changeStatusBlocare(directieId: number, compartimentId: number | null) {
        return this.http.put(`${this.changeStatusBlocareUrl}${directieId}/${compartimentId || -1}`, {}, this.httpOptions);
    }

    activeSwitch(payload: any) {
        return this.http.put(this.activeSwitchUrl, payload, this.httpOptions);
    }

    updatePrefix(directieId: number, compartimentId: number, payload: any) {
        return this.http.put(`${this.updatePrefixUrl}${directieId}/${compartimentId}`, payload, this.httpOptions);
    }

    updateDirectiiCompartimenteSerii(payload: any) {
        return this.http.post(this.updateDirectiiCompartimenteSeriiUrl, payload, this.httpOptions);
    }

    updateStatusActiveMass(payload: MassAssign) {
        return this.http.post(this.statusActiveMassUrl, payload, this.httpOptions);
    }

    updateStatusPublicMass(payload: MassAssign) {
        return this.http.post(this.statusPublicMassUrl, payload, this.httpOptions);
    }

    updateRecords() {
        return this.http.get(this.updateRecordsUrl, this.httpOptions);
    }

    // Assigned Users Methods
    getAssignedUsersListDT(directieId: number, compartimentId: number | null) {
        const compartimentParam = compartimentId ?? 0;
        const url = `${this.assignedUsersListDTUrl}/${directieId}/${compartimentParam}`;
        return this.http.get(url, this.httpOptions);
    }

    deleteAssignedUser(id: number) {
        return this.http.delete(`${this.assignedUsersDeleteUrl}/${id}`, this.httpOptions);
    }

    addAssignedUser(payload: AssignedUserPayload) {
        return this.http.post(this.assignedUsersAddUrl, payload, this.httpOptions);
    }

    setToken(token: string) {
        this.token = token;
        this.httpOptions = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: `Bearer ${this.token}`,
            }),
        };
    }
}
